import { useState, useCallback, type FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useReqdeskContext } from '../ReqdeskProvider'
import { trackTicket } from '../../api-client'
import { getTrackingTokens } from '../../storage'
import { en } from '../../i18n/en'
import { ar } from '../../i18n/ar'

const translations: Record<string, Record<string, string>> = { en, ar }

/**
 * Normalize a pasted tracking token. Copy-paste from terminals, email clients, and PDF
 * viewers commonly brings trailing whitespace, smart quotes, or BOM marks. Tokens are pure
 * ASCII lowercase-hex after the `trk_` prefix, so we can canonicalize aggressively before the
 * network round-trip without risking a false-positive normalization of a valid value.
 */
function canonicalizeToken(raw: string): string {
  let s = raw.trim()
  // Strip surrounding quotes (straight, single, and the four common smart-quote variants).
  s = s.replace(/^["'\u201C\u201D\u2018\u2019]+/, '').replace(/["'\u201C\u201D\u2018\u2019]+$/, '')
  // Strip a leading BOM if the paste came from a BOM-tagged file.
  if (s.charCodeAt(0) === 0xFEFF) s = s.slice(1)
  return s.toLowerCase()
}

interface TrackTicketViewProps {
  onTrackSuccess: (ticketId: string) => void
}

export function TrackTicketView({ onTrackSuccess }: TrackTicketViewProps) {
  const ctx = useReqdeskContext()
  const [token, setToken] = useState('')
  const savedTokens = getTrackingTokens(ctx.apiKey)

  const t = useCallback((key: string): string => {
    if (ctx.translations?.[key]) return ctx.translations[key]
    const lang = translations[ctx.language] ?? translations.en
    return lang[key] ?? key
  }, [ctx.language, ctx.translations])

  const trackMutation = useMutation({
    // The view already shows "invalid token" inline on error — suppress the global toast so
    // typing an unknown token doesn't double up with a red banner over the form.
    meta: { silent: true },
    mutationFn: (trackToken: string) => trackTicket(canonicalizeToken(trackToken)),
    onSuccess: (result) => onTrackSuccess(result.id),
  })

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const canonical = canonicalizeToken(token)
    if (canonical) trackMutation.mutate(canonical)
  }

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div className="rqd-form-group">
          <input
            className="rqd-input"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={t('track.tokenPlaceholder')}
            required
          />
          {trackMutation.isError && <div className="rqd-error-text">{t('track.invalidToken')}</div>}
        </div>
        <button className="rqd-btn rqd-btn-primary" type="submit" disabled={trackMutation.isPending || !token.trim()}>
          {trackMutation.isPending ? t('track.tracking') : t('track.submit')}
        </button>
      </form>

      {savedTokens.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div className="rqd-section-title">{t('track.recentTickets')}</div>
          <div className="rqd-ticket-list">
            {savedTokens.slice(0, 5).map(tk => (
              <button
                key={tk}
                className="rqd-ticket-card"
                style={{ border: 'none', textAlign: 'start', width: '100%' }}
                onClick={() => trackMutation.mutate(tk)}
              >
                <span className="rqd-ticket-card-number" style={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {tk.slice(0, 20)}...
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
